import React, { useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import Link from '@material-ui/core/Link';

import VariableTextField from '../../../components/variableTextField';
import { useAPI } from '../../../utils/api';

import { MODE, validMode } from '../../../utils/mode';
import { useDialog } from '../../../utils/dialog';

const parseFormData = (id, formData) => {
  if (!formData) {
    return null;
  }

  const code = formData.get('code');
  const description = formData.get('description');

  return id
    ? {
        id,
        code,
        description,
      }
    : {
        code,
        description,
      };
};

const urlPrefix = '/member/special-sub-account';

const SpecialSubAccountDetailPage = () => {
  const history = useHistory();
  const dialog = useDialog();
  const api = useAPI();
  const specialSubAccountApi = api.specialSubAccountApi;

  const { mode, dataId } = useParams();

  const mainForm = useRef();

  const [isUnmounted, setIsUnmounted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);

  const [viewDto, setViewDto] = useState({});

  // eslint-disable-next-line no-unused-vars
  const openEditMode = (id) => {
    const newPath = generatePath(`${urlPrefix}/${MODE.EDIT}/${id || ''}`);
    history.replace({ pathname: newPath });
  };

  const openViewMode = (id) => {
    const newPath = generatePath(`${urlPrefix}/${MODE.VIEW}/${id || ''}`);
    history.replace({ pathname: newPath });
  };

  const fetchDataDetail = (id) => {
    id = Number.parseInt(id, 10);

    if (id < 1) {
      return;
    }

    specialSubAccountApi
      .fetchDetail(id)
      .then((dto) => {
        setShouldPageUpdated(true);
        setViewDto(dto);
      })
      .catch((error) => {
        dialog.openAlert({ message: error.message });
      });
  };

  const submit = (event) => {
    event.preventDefault();

    if (mode === MODE.VIEW) {
      openEditMode(dataId);
      return;
    }

    const formData = new FormData(event.currentTarget);
    const data = parseFormData(dataId, formData);

    if (mode === MODE.NEW) {
      specialSubAccountApi
        .createNewData(data)
        .then((isSuccess) => {
          if (isSuccess) {
            history.replace(`${urlPrefix}/list`);
          }
        })
        .catch((error) => {
          dialog.openAlert({ message: error.message });
        });
    } else if (mode === MODE.EDIT) {
      data.id = dataId;

      specialSubAccountApi
        .updateData(data)
        .then((isSuccess) => {
          if (isSuccess) {
            fetchDataDetail(data.id);
            openViewMode();
          }
        })
        .catch((error) => {
          dialog.openAlert({ message: error.message });
        });
    }
  };

  useEffect(() => {
    if (!validMode(mode)) {
      history.replace(`${urlPrefix}/list`);
    }

    setShouldPageUpdated(true);
  }, [mode, history]);

  useEffect(() => {
    if (isUnmounted) {
      return;
    }

    return () => {
      setIsUnmounted(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldPageUpdated && mode === MODE.NEW) {
      setViewDto({});
    }

    return () => {
      setShouldPageUpdated(false);
    };
  }, [mode, shouldPageUpdated]);

  useEffect(() => {
    if (isMounted) {
      return;
    }

    if (mode === MODE.VIEW || mode === MODE.EDIT) {
      if (dataId && dataId !== 'undefined') {
        fetchDataDetail(dataId);
        setIsMounted(true);
      }
    } else if (mode === MODE.NEW) {
      setIsMounted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!shouldPageUpdated && (mode === MODE.NEW || viewDto) ? (
        <>
          <Box ref={mainForm} component="form" noValidate onSubmit={submit}>
            <Paper
              elevation={4}
              sx={{
                paddingTop: { xs: 1, md: 2 },
                paddingBottom: { xs: 2, md: 4 },
                marginBottom: 4,
                maxWidth: '900px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <Container>
                <Typography variant="h6" marginBottom={4}>
                  特殊分帳設定
                </Typography>

                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="code"
                      label="代碼"
                      text={viewDto.code}
                      required={true}
                      inputProps={{ maxLength: 30 }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={12}>
                    <VariableTextField
                      mode={mode}
                      name="description"
                      label="描述"
                      text={viewDto.description}
                      required={false}
                      inputProps={{ maxLength: 300 }}
                    />
                  </Grid>
                </Grid>
              </Container>
            </Paper>

            <Grid
              container
              spacing={3}
              sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
            >
              {mode !== MODE.VIEW ? (
                <Grid item xs={12} sx={{ maxWidth: '200px' }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="success"
                    sx={{ color: 'white' }}
                  >
                    {mode === MODE.NEW ? '新增' : '儲存'}
                  </Button>
                </Grid>
              ) : (
                <>
                  <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                    >
                      編輯
                    </Button>
                  </Grid>
                </>
              )}
              <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                <Link underline="none" href={`/#${urlPrefix}/list`}>
                  <Button
                    type="button"
                    fullWidth
                    variant="contained"
                    color="primary"
                  >
                    返回列表頁
                  </Button>
                </Link>
              </Grid>
            </Grid>
          </Box>
        </>
      ) : undefined}
    </>
  );
};

export default SpecialSubAccountDetailPage;
